import { Popup } from 'components/Popup'
import { Button, BUTTON_TYPES } from '../../../../components/Button'
import { BTN_TXT, TypeError } from '../../../../constants'
import { TAddBusinessAccountForm, TAddBusinessDetailsProps } from './types'
import { Input } from '../../../../components/Input'
import { Tooltip } from '../../../../components/Tooltip/Tooltip'
import { ReactComponent as InformationIcon } from 'assets/svg/Information.svg'
import cn from 'classnames'
import { Form } from 'antd'
import { CustomForm } from 'components/Forms/CustomForm'
import Typography from '../../../../components/Typography/Typography'
import { Checkbox } from '../../../../components/Checkbox'
import { useEffect, useState } from 'react'
import { setLoading } from '../../../../redux/store/common/slice'
import { useSelector } from 'react-redux'
import { setBusinessDetailsVendorOrganization } from '../api'
import { initialValues } from './constants'
import { notification } from '../../../../components/Notification'
import { AxiosResponse } from 'axios'
import { fetchVendorInfo } from '../../../../redux/store/vendorInfo/slice'
import { getUser } from '../../../../redux/store/user/getters'
import { useAppDispatch } from '../../../../redux/createStore'
import { getVendorInfo } from '../../../../redux/store/vendorInfo/getters'
import { routes } from '../../../../router'
import { TABS_OPTIONS_KEY } from '../constants'
import useRouter from '../../../../hooks/useRouter'
import { notification as antdNotification } from 'antd'

export const AddBusinessDetails = ({
  isPopupVisible,
  setIsPopupVisible
}: TAddBusinessDetailsProps) => {
  const dispatch = useAppDispatch()
  const { push } = useRouter()
  const [form] = Form.useForm()
  const [errors, setErrors] = useState<TypeError>({})
  const user = useSelector(getUser)
  const vendor = useSelector(getVendorInfo)
  const handleClose = () => {
    form.resetFields()
    setErrors({})
    setIsPopupVisible(false)
  }

  useEffect(() => {
    if (isPopupVisible) {
      form.setFieldsValue({
        legal_business_name: vendor.name
      })
    }
  }, [form, vendor.name, isPopupVisible])

  const handleSubmit = async (values: TAddBusinessAccountForm) => {
    if (!values.consent) {
      setErrors({
        consent: ['Consent is required to create a business account.']
      })
      notification.error({
        message: 'Consent is required to create a business account.'
      })
      return
    }
    try {
      dispatch(setLoading(true))
      await setBusinessDetailsVendorOrganization({
        legal_business_name: values.legal_business_name
      })

      if (vendor.name !== values.legal_business_name) {
        notification.info({
          message: `The legal name has been verified as ${values.legal_business_name}. This name can be applied to the Vendor Organization Profile.`,
          key: 'business-account-setup',
          btn: (
            <div className="row justify-end gap-8">
              <Button
                type={BUTTON_TYPES.DEFAULT}
                onClick={() => antdNotification.close('business-account-setup')}
                upperCase
              >
                {BTN_TXT.CANCEL}
              </Button>
              <Button
                type={BUTTON_TYPES.PRIMARY}
                onClick={() =>
                  push(
                    `${routes.myCommunity}${routes.myOrganization}/${TABS_OPTIONS_KEY.general}`
                  )
                }
                upperCase
                inline
              >
                {BTN_TXT.GO_TO_ORGANIZATION_DETAILS}
              </Button>
            </div>
          )
        })
      }
      if (user.vendor) {
        dispatch(fetchVendorInfo(user?.vendor))
      }
      setIsPopupVisible(false)
      form.resetFields()
    } catch (e) {
      const err = e as AxiosResponse
      console.error(err)
      setErrors(err.data)
      if (err?.data?.details) {
        notification.error({
          message: err?.data.details
        })
      } else if (!!err?.data?.non_field_errors.length) {
        notification.error({
          message: err?.data.non_field_errors[0]
        })
      } else if (!!err?.data?.length) {
        notification.error({
          message: err?.data[0]
        })
      }
    } finally {
      dispatch(setLoading(false))
    }
  }

  return (
    <Popup
      visible={isPopupVisible}
      onCancel={handleClose}
      title={`Add business details`}
      width={676}
      footer={
        <>
          <Button type={BUTTON_TYPES.DEFAULT} onClick={handleClose} upperCase>
            {BTN_TXT.CANCEL}
          </Button>
          <Button
            type={BUTTON_TYPES.PRIMARY}
            htmlType="submit"
            form="add-business-account-form"
            upperCase
          >
            {BTN_TXT.VERIFY}
          </Button>
        </>
      }
    >
      <CustomForm<TAddBusinessAccountForm>
        className={cn('forms-sign-in fa-form')}
        layout="vertical"
        initialValues={initialValues}
        form={form}
        onFinish={handleSubmit}
        id="add-business-account-form"
        onChange={() => {
          setErrors({})
        }}
      >
        <Form.Item shouldUpdate>
          {() => {
            const fieldError = form.getFieldError('legal_business_name')

            // Show API error if exists, otherwise show validation error
            const helpMessage = !!errors.legal_business_name?.length
              ? errors.legal_business_name
              : fieldError.length > 0
              ? 'Legal business name field is required'
              : undefined

            const hasError =
              errors.legal_business_name?.length || fieldError.length > 0

            return (
              <Input
                propsItem={{
                  name: 'legal_business_name',
                  help: helpMessage || helpMessage?.[0],
                  validateStatus: hasError ? 'error' : undefined,
                  label: (
                    <div className="row">
                      Legal business name&nbsp;
                      <Tooltip
                        title={
                          'This must match official documents exactly (DBAs are not permitted)'
                        }
                      >
                        <InformationIcon />
                      </Tooltip>
                      &nbsp;
                    </div>
                  ),
                  rules: [
                    {
                      max: 64,
                      required: true
                    }
                  ]
                }}
                propsInput={{
                  showCount: true,
                  maxLength: 64
                }}
              />
            )
          }}
        </Form.Item>
        <div className="mt-32" />
        <div className="separator" />
        <Typography.Body1 className="mt-32 mb-16">
          To enable electronic ACH payments, we will securely transmit your
          Legal business name to our payment partner. By continuing, you consent
          to share this information for the purpose of account creation.
        </Typography.Body1>
        <Checkbox
          propsItem={{
            name: 'consent',
            help: '',
            validateStatus: errors.consent ? 'error' : undefined,
            rules: [
              {
                required: true
              }
            ]
          }}
          propsCheckbox={{
            onChange: (e) => {
              form.setFieldsValue({
                consent: e.target.checked
              })
            }
          }}
        >
          I consent.
        </Checkbox>
      </CustomForm>
    </Popup>
  )
}
